import React, { memo } from "react";
import { Text, View, Page, Document, PDFViewer } from "@react-pdf/renderer";
import "../pdf/Fonts.js";

const AnimalRelativesPdf = ({
    selectedOwner,
    selectedAnimal,
    fetchedData,
}) => {
    // Inline styles
    const styles = {
        pdfViewer: { width: '85vw', height: '100vh' },
        page: {
            padding: 40,
            fontFamily: "Roboto",
            backgroundColor: 'white'
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: 15
        },
        header: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 15,
            backgroundColor: '#000000',
            padding: 15,
            borderRadius: 5
        },
        title: {
            fontSize: 18,
            fontWeight: 'bold',
            color: 'white',
            textAlign: 'center'
        },
        animalInfoContainer: {
            marginBottom: 15,
            border: '1pt solid #000',
            borderRadius: 5,
            overflow: 'hidden'
        },
        animalHeader: {
            backgroundColor: '#000000',
            padding: 8,
            fontSize: 14,
            fontWeight: 'bold',
            color: 'white'
        },
        tableRow: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1pt solid #DDD',
        },
        tableCellHeader: {
            flex: 1,
            fontSize: 8,
            padding: 5,
            textAlign: 'center',
            fontWeight: 'bold',
            backgroundColor: '#EEEEEE',
            borderRight: '1pt solid #DDD'
        },
        tableCell: {
            flex: 1,
            fontSize: 8,
            padding: 5,
            textAlign: 'center',
            borderRight: '1pt solid #DDD'
        },
        noDataText: {
            fontSize: 10,
            color: '#555555',
            padding: 10,
            textAlign: 'center'
        },
        sectionHeader: {
            backgroundColor: '#000000',
            color: 'white',
            fontSize: 14,
            fontWeight: 'bold',
            padding: 8,
            marginBottom: 10,
            marginTop: 5,
            borderRadius: 3
        }
    };

    // Helper to format dates with fallback
    const formatDate = (dateString) => {
        if (!dateString) return "—";
        try {
            return new Date(dateString).toLocaleDateString("el-GR");
        } catch (e) {
            return "—";
        }
    };

    // Helper to get sex text in Greek
    const getSexText = (sex) => {
        if (!sex) return "—";
        return sex === 'Άρρεν' ? 'Άρρεν' : 'Θήλυ';
    };

    // Helper to safely display text
    const safeText = (text) => {
        return text || "—";
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <PDFViewer style={styles.pdfViewer}>
                <Document>
                    <Page size='A4' style={styles.page} wrap>
                        <View style={styles.container}>
                            {/* Title Page */}
                            <View style={styles.header}>
                                <Text style={styles.title}>Συγγενείς Ζώου {safeText(selectedAnimal?.registrationCode)}</Text>
                            </View>

                            {/* Owner Information */}
                            <View style={{
                                ...styles.animalInfoContainer,
                                padding: 10,
                                backgroundColor: '#F5F5F5',
                                borderLeft: '3pt solid #000'
                            }}>
                                <Text style={{ fontSize: 10 }}>
                                    <Text style={{ fontWeight: 'bold' }}>Μονάδα: </Text>
                                    {safeText(selectedOwner?.lastName) + " " + safeText(selectedOwner?.firstName)}
                                </Text>
                            </View>

                            {/* Animal Information */}
                            <View style={styles.animalInfoContainer}>
                                <View style={styles.animalHeader}>
                                    <Text>Στοιχεία Ζώου</Text>
                                </View>

                                {/* Table Header Row */}
                                <View style={styles.tableRow}>
                                    <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                    <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                    <Text style={styles.tableCellHeader}>Φύλο</Text>
                                    <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                    <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                    <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                </View>

                                {/* Data Row */}
                                <View style={styles.tableRow}>
                                    <Text style={styles.tableCell}>{safeText(selectedAnimal?.registrationCode)}</Text>
                                    <Text style={styles.tableCell}>{safeText(selectedAnimal?.flockCode)}</Text>
                                    <Text style={styles.tableCell}>{getSexText(selectedAnimal?.sex)}</Text>
                                    <Text style={styles.tableCell}>{formatDate(selectedAnimal?.birthDate)}</Text>
                                    <Text style={styles.tableCell}>{safeText(selectedOwner?.code)}</Text>
                                    <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(selectedOwner?.lastName)} ${safeText(selectedOwner?.firstName)}`}</Text>
                                </View>
                            </View>

                            {/* Mother's Information */}
                            {fetchedData?.mother?.animal && (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Μητέρας Ζώου</Text>
                                    </View>

                                    {/* Table Header Row */}
                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.mother?.animal?.registrationCode)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.mother?.animal?.flockCode)}</Text>
                                        <Text style={styles.tableCell}>{getSexText(fetchedData?.mother?.animal?.sex)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(fetchedData?.mother?.animal?.birthDate)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.mother?.owner?.code)}</Text>
                                        <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(fetchedData?.mother?.owner?.lastName)} ${safeText(fetchedData?.mother?.owner?.firstName)}`}</Text>
                                    </View>
                                </View>
                            )}

                            {/* Maternal Grandmother */}
                            {fetchedData?.maternalGrandmother?.animal && (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Μητέρας Μητέρας</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.maternalGrandmother?.animal?.registrationCode)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.maternalGrandmother?.animal?.flockCode)}</Text>
                                        <Text style={styles.tableCell}>{getSexText(fetchedData?.maternalGrandmother?.animal?.sex)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(fetchedData?.maternalGrandmother?.animal?.birthDate)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.maternalGrandmother?.owner?.code)}</Text>
                                        <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(fetchedData?.maternalGrandmother?.owner?.lastName)} ${safeText(fetchedData?.maternalGrandmother?.owner?.firstName)}`}</Text>
                                    </View>
                                </View>
                            )}

                            {/* Maternal Grandfather */}
                            {fetchedData?.maternalGrandfather?.animal && (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Πατέρα Μητέρας</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.maternalGrandfather?.animal?.registrationCode)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.maternalGrandfather?.animal?.flockCode)}</Text>
                                        <Text style={styles.tableCell}>{getSexText(fetchedData?.maternalGrandfather?.animal?.sex)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(fetchedData?.maternalGrandfather?.animal?.birthDate)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.maternalGrandfather?.owner?.code)}</Text>
                                        <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(fetchedData?.maternalGrandfather?.owner?.lastName)} ${safeText(fetchedData?.maternalGrandfather?.owner?.firstName)}`}</Text>
                                    </View>
                                </View>
                            )}

                            {/* Father's Information */}
                            {fetchedData?.father?.animal && (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Πατέρα Ζώου</Text>
                                    </View>

                                    {/* Table Header Row */}
                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.father?.animal?.registrationCode)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.father?.animal?.flockCode)}</Text>
                                        <Text style={styles.tableCell}>{getSexText(fetchedData?.father?.animal?.sex)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(fetchedData?.father?.animal?.birthDate)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.father?.owner?.code)}</Text>
                                        <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(fetchedData?.father?.owner?.lastName)} ${safeText(fetchedData?.father?.owner?.firstName)}`}</Text>
                                    </View>
                                </View>
                            )}

                            {/* Paternal Grandmother */}
                            {fetchedData?.paternalGrandmother?.animal && (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Μητέρας Πατέρα</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.paternalGrandmother?.animal?.registrationCode)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.paternalGrandmother?.animal?.flockCode)}</Text>
                                        <Text style={styles.tableCell}>{getSexText(fetchedData?.paternalGrandmother?.animal?.sex)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(fetchedData?.paternalGrandmother?.animal?.birthDate)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.paternalGrandmother?.owner?.code)}</Text>
                                        <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(fetchedData?.paternalGrandmother?.owner?.lastName)} ${safeText(fetchedData?.paternalGrandmother?.owner?.firstName)}`}</Text>
                                    </View>
                                </View>
                            )}

                            {/* Paternal Grandfather */}
                            {fetchedData?.paternalGrandfather?.animal && (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Πατέρα Πατέρα</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.paternalGrandfather?.animal?.registrationCode)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.paternalGrandfather?.animal?.flockCode)}</Text>
                                        <Text style={styles.tableCell}>{getSexText(fetchedData?.paternalGrandfather?.animal?.sex)}</Text>
                                        <Text style={styles.tableCell}>{formatDate(fetchedData?.paternalGrandfather?.animal?.birthDate)}</Text>
                                        <Text style={styles.tableCell}>{safeText(fetchedData?.paternalGrandfather?.owner?.code)}</Text>
                                        <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(fetchedData?.paternalGrandfather?.owner?.lastName)} ${safeText(fetchedData?.paternalGrandfather?.owner?.firstName)}`}</Text>
                                    </View>
                                </View>
                            )}

                            {/* Paternal Half Siblings */}
                            {fetchedData?.paternalHalfSiblings?.length > 0 ? (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Ετεροθαλών Αδερφών Ζώου Από την Μεριά του Πατέρα</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    {fetchedData?.paternalHalfSiblings?.map((sibling, index) => (
                                        <View key={sibling.animal._id || index} style={{
                                            ...styles.tableRow,
                                            backgroundColor: index % 2 === 0 ? '#F8F9FA' : '#FFFFFF'
                                        }}>
                                            <Text style={styles.tableCell}>{safeText(sibling.animal.registrationCode)}</Text>
                                            <Text style={styles.tableCell}>{safeText(sibling.animal.flockCode)}</Text>
                                            <Text style={styles.tableCell}>{getSexText(sibling.animal.sex)}</Text>
                                            <Text style={styles.tableCell}>{formatDate(sibling.animal.birthDate)}</Text>
                                            <Text style={styles.tableCell}>{safeText(sibling?.owner?.code)}</Text>
                                            <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(sibling?.owner?.lastName)} ${safeText(sibling?.owner?.firstName)}`}</Text>
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <View style={{ ...styles.animalInfoContainer, padding: 10 }}>
                                    <Text style={styles.noDataText}>Δεν υπάρχουν στοιχεία Ετεροθαλών Αδερφών Ζώου Από την Μεριά του Πατέρα</Text>
                                </View>
                            )}

                            {/* Maternal Half Siblings */}
                            {fetchedData?.maternalHalfSiblings?.length > 0 ? (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Ετεροθαλών Αδερφών Ζώου Από την Μεριά της Μητέρας</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    {fetchedData?.maternalHalfSiblings?.map((sibling, index) => (
                                        <View key={sibling.animal._id || index} style={{
                                            ...styles.tableRow,
                                            backgroundColor: index % 2 === 0 ? '#F8F9FA' : '#FFFFFF'
                                        }}>
                                            <Text style={styles.tableCell}>{safeText(sibling.animal.registrationCode)}</Text>
                                            <Text style={styles.tableCell}>{safeText(sibling.animal.flockCode)}</Text>
                                            <Text style={styles.tableCell}>{getSexText(sibling.animal.sex)}</Text>
                                            <Text style={styles.tableCell}>{formatDate(sibling.animal.birthDate)}</Text>
                                            <Text style={styles.tableCell}>{safeText(sibling?.owner?.code)}</Text>
                                            <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(sibling?.owner?.lastName)} ${safeText(sibling?.owner?.firstName)}`}</Text>
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <View style={{ ...styles.animalInfoContainer, padding: 10 }}>
                                    <Text style={styles.noDataText}>Δεν υπάρχουν στοιχεία Ετεροθαλών Αδερφών Ζώου Από την Μεριά της Μητέρας</Text>
                                </View>
                            )}

                            {/* Daughters Section */}
                            {fetchedData?.daughters?.length > 0 || fetchedData?.sons?.length > 0 ? (
                                <View style={styles.animalInfoContainer}>
                                    <View style={styles.animalHeader}>
                                        <Text>Στοιχεία Παιδιών</Text>
                                    </View>

                                    <View style={styles.tableRow}>
                                        <Text style={styles.tableCellHeader}>Κωδικός Γενεολογικού Βιβλίου</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός στην εκτροφή</Text>
                                        <Text style={styles.tableCellHeader}>Φύλο</Text>
                                        <Text style={styles.tableCellHeader}>Ημ/νία Γέννησης</Text>
                                        <Text style={styles.tableCellHeader}>Κωδικός εκτροφής</Text>
                                        <Text style={{ ...styles.tableCellHeader, borderRight: 'none' }}>Επωνυμία εκτροφής</Text>
                                    </View>

                                    {fetchedData?.daughters?.map((daughter, index) => (
                                        <View key={daughter.animal._id || index} style={{
                                            ...styles.tableRow,
                                            backgroundColor: index % 2 === 0 ? '#F8F9FA' : '#FFFFFF'
                                        }}>
                                            <Text style={styles.tableCell}>{safeText(daughter.animal.registrationCode)}</Text>
                                            <Text style={styles.tableCell}>{safeText(daughter.animal.flockCode)}</Text>
                                            <Text style={styles.tableCell}>{getSexText(daughter.animal.sex)}</Text>
                                            <Text style={styles.tableCell}>{formatDate(daughter.animal.birthDate)}</Text>
                                            <Text style={styles.tableCell}>{safeText(daughter?.owner?.code)}</Text>
                                            <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(daughter?.owner?.lastName)} ${safeText(daughter?.owner?.firstName)}`}</Text>
                                        </View>
                                    ))}
                                    {fetchedData?.sons?.map((son, index) => (
                                        <View key={son.animal._id || index} style={{
                                            ...styles.tableRow,
                                            backgroundColor: index % 2 === 0 ? '#F8F9FA' : '#FFFFFF'
                                        }}>
                                            <Text style={styles.tableCell}>{safeText(son.animal.registrationCode)}</Text>
                                            <Text style={styles.tableCell}>{safeText(son.animal.flockCode)}</Text>
                                            <Text style={styles.tableCell}>{getSexText(son.animal.sex)}</Text>
                                            <Text style={styles.tableCell}>{formatDate(son.animal.birthDate)}</Text>
                                            <Text style={styles.tableCell}>{safeText(son?.owner?.code)}</Text>
                                            <Text style={{ ...styles.tableCell, borderRight: 'none' }}>{`${safeText(son?.owner?.lastName)} ${safeText(son?.owner?.firstName)}`}</Text>
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <View style={{ ...styles.animalInfoContainer, padding: 10 }}>
                                    <Text style={styles.noDataText}>Δεν υπάρχουν στοιχεία για παιδιά</Text>
                                </View>
                            )}
                        </View>
                    </Page>
                </Document>
            </PDFViewer>
        </div>
    );
};

export default memo(AnimalRelativesPdf);